import React from "react";
import {
  Calendar,
  Callout,
  DayOfWeek,
  DefaultButton,
  DirectionalHint,
  FocusTrapZone,
  IBasePickerSuggestionsProps,
  ITag,
  PrimaryButton,
  Stack,
  TagPicker,
  Text,
  TextField,
} from "@fluentui/react";
import { Formik, FormikErrors } from "formik";
import Icon from "@mdi/react";
import XLLogo from "../../../images/xl.png";
import { IMiscellaneousFile } from "../../../vm";
import { ToastContext } from "../../common/ToastProvider";
import {
  addMiscellaneousFile,
  updateMiscellaneousFile,
  uploadMiscellaneousFile,
} from "../../../services/customers/CustomerService";
import CustomDrawer from "../../common/CustomDrawer";
import {
  childrenGap,
  DayPickerStrings,
  FILE_UPLOAD_CONFIG,
  FILE_UPLOAD_MODULES,
} from "../../common/Constants";
import moment from "moment";
import { mdiCloseCircle } from "@mdi/js";
import { THEME } from "../../../utils/Styles";
import {
  getFileTypeErrorMessage,
  validateFileType,
} from "../../../services/UtilService";

let calendarButtonElement: HTMLElement;

export interface ManageMiscellaneousFilesDialogProps {
  handleLoading: any;
  handleDialogClose: any;
  customerId: string;
  data?: IMiscellaneousFile;
  // datacenterId?: string;
  datacenterList: ITag[];
  miscellaneousFiles: IMiscellaneousFile[];
  date: Date | string;
  type?: "misc" | "power";
}

const ManageMiscellaneousFilesDialog: React.FC<
  ManageMiscellaneousFilesDialogProps
> = ({
  handleDialogClose,
  handleLoading,
  data,
  customerId,
  datacenterList,
  miscellaneousFiles,
  date,
  type = "misc",
}) => {
  //custom hooks
  const { showToast } = React.useContext(ToastContext);

  // file upload config
  const fileUploadConfig =
    FILE_UPLOAD_CONFIG[
      type === "misc"
        ? FILE_UPLOAD_MODULES.MISCELLANEOUS_FILES
        : FILE_UPLOAD_MODULES.MISCELLANEOUS_FILES_POWER
    ];

  //state
  const [state, setState] = React.useState({
    miscellaneousFiles: {
      datacenterId: "",
      date: new Date(date),
      file: undefined,
      customerId: customerId,
      type,
      deletedFile: "",
      ...data,
    },
    showCallender: false,
    selectedFiles: undefined,
    selectedDatacenter: [],
  } as {
    miscellaneousFiles: IMiscellaneousFile;
    showCallender: boolean;
    selectedFiles?: File[];
    selectedDatacenter: ITag[];
  });

  //   hooks
  React.useEffect(() => {
    if (data && data.datacenterId !== "" && datacenterList) {
      let selectedDatacenter: ITag[] = [];
      datacenterList.forEach((ele) => {
        if (ele.key === data.datacenterId) {
          selectedDatacenter.push(ele);
        }
      });
      setState({
        ...state,
        selectedDatacenter,
      });
    }
  }, []);

  const closeAddDialog = (data?: any) => {
    handleDialogClose(data);
  };

  const handleFileChange = (files: any) => {
    // console.log(typeof files[0]);

    if (files && files.length > 0) {
      // console.log("f", files[0]);
      // Check if all files are of valid type
      const allValid = Array.from(files as any[]).every((file) =>
        validateFileType(file, fileUploadConfig.acceptedFileTypes)
      );

      if (!allValid) {
        showToast(
          getFileTypeErrorMessage(fileUploadConfig.fileTypeDescription),
          "error"
        );
      }
      setState({
        ...state,
        selectedFiles: files,
      });
    } else {
      showToast("Select only valid file", "error");
    }
  };

  const closeCallender = (ev?: any) => {
    setState({
      ...state,
      showCallender: false,
    });
  };

  const uploadFile = async (
    fileName: string,
    file: File,
    type: "misc" | "power"
  ) => {
    handleLoading();
    let result = await uploadMiscellaneousFile(file, type);
    handleLoading();
    if (result.success) {
      return result?.data?.fileName || "";
      // return fileName;
    } else {
      showToast(result.message || `Error while uploading file`, "error");
    }
    return undefined;
  };

  const deleteImage = (deletedFile: string, values: any) => {
    setState({
      ...state,
      miscellaneousFiles: {
        ...values,
        file: undefined,
        deletedFile,
      },
      selectedFiles: undefined,
    });
  };

  const datacenterPickerSuggestionsProps: IBasePickerSuggestionsProps = {
    noResultsFoundText: "No datacenter found",
  };

  const filterSuggestedTags = (filterText: string, tagList: ITag[]): ITag[] => {
    // let miscellaneousFile = [...miscellaneousFiles];
    let allDataCenterList = [...datacenterList];

    // miscellaneousFile.forEach((item) => {
    //   allDataCenterList = allDataCenterList.filter(
    //     (ele) => ele.key !== item.datacenterId
    //   );
    // });

    return filterText
      ? allDataCenterList.filter(
          (tag) =>
            tag.name.toLowerCase().indexOf(filterText.toLowerCase()) === 0 &&
            !listContainsTagList(tag, tagList)
        )
      : [];
  };

  const showAllTags = (selectedItems?: ITag[] | undefined) => {
    // let miscellaneousFile = [...miscellaneousFiles];
    let allDataCenterList = datacenterList ? [...datacenterList] : [];

    // miscellaneousFile.forEach((item) => {
    //   allDataCenterList = allDataCenterList.filter(
    //     (ele) => ele.key !== item.datacenterId
    //   );
    // });

    // console.log(allDataCenterList);
    return removeDuplicates(allDataCenterList, selectedItems);
  };

  function removeDuplicates(tagList: ITag[], tags?: ITag[]) {
    return tagList.filter((tag) => !listContainsTagList(tag, tags));
  }

  const listContainsTagList = (tag: ITag, tagList?: ITag[]) => {
    if (!tagList || !tagList.length || tagList.length === 0) {
      return false;
    }
    return tagList.some((compareTag) => compareTag.key === tag.key);
  };

  return (
    <React.Fragment>
      <CustomDrawer
        title={
          data?._id
            ? `Update ${type === "misc" ? "Others" : "Power"} File`
            : `Add ${type === "misc" ? "Others" : "Power"} File`
        }
        handelOpen={true}
        onClose={() => closeAddDialog()}
      >
        <Stack>
          <Formik
            enableReinitialize
            initialValues={state.miscellaneousFiles}
            validate={(values) => {
              let errors: FormikErrors<IMiscellaneousFile> = {};
              if (!values.date) {
                errors.date = "Required";
              }
              if (!values.file && !state.selectedFiles) {
                errors.file = "Required";
              } else if (state.selectedFiles) {
                const allValid = Array.from(state.selectedFiles).every((file) =>
                  validateFileType(file, fileUploadConfig.acceptedFileTypes)
                );

                if (!allValid) {
                  errors.file = getFileTypeErrorMessage(
                    fileUploadConfig.fileTypeDescription
                  );
                }
              }
              if (!values.datacenterId) {
                errors.datacenterId = "Required";
              }
              return errors;
            }}
            onSubmit={async (values: IMiscellaneousFile, { setSubmitting }) => {
              let obj = { ...values };
              obj.date = moment(obj.date).format("YYYY-MM-DD");
              let result;
              const dataArr: IMiscellaneousFile[] = [];

              if (state.selectedFiles && state.selectedFiles.length > 0) {
                if (values._id) {
                  // while updating, user can select only one
                  let name =
                    new Date().getTime() +
                    "-" +
                    state.selectedFiles[0].name.toLowerCase().replace(/ /g, "");
                  let fileName = await uploadFile(
                    name,
                    state.selectedFiles[0],
                    type
                  );
                  if (fileName) {
                    obj.file = fileName;
                  } else return;
                } else {
                  // while adding, user can add/select multiple file at once
                  for (let i = 0; i < state.selectedFiles.length; i++) {
                    let dataObj = { ...obj };
                    const file = state.selectedFiles[i];
                    let name =
                      new Date().getTime() +
                      "-" +
                      file.name.toLowerCase().replace(/ /g, "");
                    let fileName = await uploadFile(name, file, type);
                    if (fileName) {
                      dataObj.file = fileName;
                    } else return;
                    // pushing data to arr
                    dataArr.push(dataObj);
                  }
                }
              }
              handleLoading();
              if (values?._id) {
                result = await updateMiscellaneousFile(obj);
              } else {
                result = await addMiscellaneousFile(dataArr);
              }
              handleLoading();
              if (result?.success) {
                closeAddDialog(result.data);
              } else {
                showToast(
                  result?.message || `Error while uploading file`,
                  "error"
                );
              }
              setSubmitting(false);
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              setFieldValue,
            }) => (
              <form onSubmit={handleSubmit}>
                <Stack wrap tokens={childrenGap}>
                  <Stack.Item>
                    <div className="ms-Grid p-0">
                      <div className="ms-Grid-row">
                        <div className="ms-Grid-col ms-sm12 ms-md2 ms-lg3 ms-xl3 ms-xxl3">
                          <div
                            ref={(calendarBtn) =>
                              (calendarButtonElement = calendarBtn!)
                            }
                          >
                            <DefaultButton
                              text={
                                !values.date
                                  ? "Select a month"
                                  : moment(values.date).format("MMM-YYYY")
                              }
                              onClick={() =>
                                setState({ ...state, showCallender: true })
                              }
                              className="month-picker-btn"
                            />
                          </div>
                          {state.showCallender && (
                            <Callout
                              isBeakVisible={false}
                              gapSpace={0}
                              doNotLayer={false}
                              target={calendarButtonElement}
                              directionalHint={DirectionalHint.bottomLeftEdge}
                              onDismiss={() => closeCallender()}
                              setInitialFocus
                            >
                              <FocusTrapZone
                                firstFocusableSelector="ms-DatePicker-day--today"
                                isClickableOutsideFocusTrap
                              >
                                <Calendar
                                  // eslint-disable-next-line react/jsx-no-bind
                                  onSelectDate={(
                                    date: Date | null | undefined
                                  ) => {
                                    setFieldValue("date", date);
                                    setState({
                                      ...state,
                                      showCallender: false,
                                    });
                                  }}
                                  onDismiss={() => closeCallender()}
                                  isMonthPickerVisible={true}
                                  value={new Date(values.date) as Date}
                                  firstDayOfWeek={DayOfWeek.Sunday}
                                  strings={DayPickerStrings}
                                  isDayPickerVisible={false}
                                  highlightCurrentMonth={false}
                                  highlightSelectedMonth={true}
                                  showMonthPickerAsOverlay={false}
                                  showGoToToday={false}
                                />
                              </FocusTrapZone>
                            </Callout>
                          )}
                        </div>
                        {!values.file && (
                          <div className="ms-Grid-col ms-sm12 ms-md10 ms-lg9 ms-xl9 ms-xxl9 custom-textfield">
                            <TextField
                              name="file"
                              type="file"
                              accept={fileUploadConfig.acceptedFileTypes}
                              multiple={
                                data?._id || type === "power" ? false : true
                              }
                              onChange={(event: any) =>
                                handleFileChange(event.target.files)
                              }
                              onBlur={handleBlur}
                              label="Select a file"
                              errorMessage={
                                errors.file && touched.file
                                  ? (errors.file as string)
                                  : ""
                              }
                              description={fileUploadConfig.hintText}
                              className="file-selector"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </Stack.Item>
                  {values.file && (
                    <Stack.Item styles={{ root: { marginBottom: 50 } }}>
                      <div className="ms-Grid">
                        <div className="ms-Grid-row">
                          <div className="ms-Grid-col ms-sm12 ms-md6 ms-lg4 ms-xl4 ms-xxl4 c-height">
                            <img
                              className="w-100"
                              src={XLLogo}
                              alt="log"
                              style={{ marginLeft: -22 }}
                            />
                            <span
                              className="img-delete-icon"
                              onClick={() => deleteImage(values.file, values)}
                            >
                              <Icon
                                path={mdiCloseCircle}
                                size={0.8}
                                className="pointer"
                                color={THEME.palette.themePrimary}
                              />
                            </span>
                          </div>
                        </div>
                      </div>
                    </Stack.Item>
                  )}
                  <Stack.Item className="custom-picker">
                    <TagPicker
                      removeButtonAriaLabel="Remove"
                      itemLimit={1}
                      defaultSelectedItems={state.selectedDatacenter}
                      onResolveSuggestions={filterSuggestedTags as any}
                      pickerSuggestionsProps={datacenterPickerSuggestionsProps}
                      onEmptyResolveSuggestions={showAllTags}
                      // getTextFromItem={getTextFromItem}
                      inputProps={{
                        placeholder: "Select a particular datacenter",
                      }}
                      onChange={(items: any) => {
                        let datacenterId: string = "";
                        items.forEach((ele: any) => {
                          datacenterId = ele.key;
                        });
                        setFieldValue("datacenterId", datacenterId);
                        // setState({
                        //   ...state,
                        //   selectedDatacenters: items,
                        //   customerEventSources: {
                        //     ...state.customerEventSources,
                        //     datacenterId,
                        //   },
                        // });
                      }}
                      disabled={data && data.datacenterId !== ""}
                    />
                    {errors.datacenterId && touched.datacenterId && (
                      <Text className="error-msg">{errors.datacenterId}</Text>
                    )}
                  </Stack.Item>

                  <Stack.Item>
                    <Stack
                      horizontal
                      wrap
                      tokens={childrenGap}
                      horizontalAlign="end"
                    >
                      <DefaultButton
                        text="Cancel"
                        onClick={() => closeAddDialog()}
                        className="borderless-btn"
                      />
                      <PrimaryButton
                        type="submit"
                        text={data?._id ? "Update" : "Add"}
                      />
                    </Stack>
                  </Stack.Item>
                </Stack>
              </form>
            )}
          </Formik>
        </Stack>
      </CustomDrawer>
    </React.Fragment>
  );
};

export default ManageMiscellaneousFilesDialog;
